import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { X, ScanLine, Loader2, Camera } from 'lucide-react';
import { Button } from './ui/button';

/**
 * BarcodeScannerModal — opens the device camera and runs a live ZXing reader
 * to decode barcodes (Code 128, EAN, QR, etc).
 *
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   onResult   (text: string) => void  // called when a code is read
 *   filter     (text: string) => boolean  // optional — only accept matching codes
 *   helperText string
 */
const BarcodeScannerModal = ({ open, onClose, onResult, filter, helperText }) => {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const [status, setStatus] = useState('starting'); // starting | scanning | error
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStatus('starting');
    setErrMsg('');

    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    (async () => {
      try {
        // Prefer the back camera on phones — falls back to default automatically.
        const constraints = {
          video: { facingMode: { ideal: 'environment' } },
        };
        const controls = await reader.decodeFromConstraints(
          constraints,
          videoRef.current,
          (result, err) => {
            if (cancelled) return;
            if (result) {
              const text = result.getText();
              if (filter && !filter(text)) {
                // ignore — keep scanning
                return;
              }
              onResult(text);
              try { controls.stop(); } catch { /* noop */ }
            }
            // err is fired on every empty frame — only surface non-NotFound errors.
            if (err && err.name && err.name !== 'NotFoundException' && err.name !== 'ChecksumException') {
              // soft-ignore, keep scanning
            }
          },
        );
        controlsRef.current = controls;
        if (!cancelled) setStatus('scanning');
      } catch (err) {
        if (cancelled) return;
        const name = err?.name || '';
        let msg = err?.message || 'Could not access camera.';
        if (name === 'NotAllowedError') {
          msg = 'Camera permission was denied. Tap the camera icon in your browser address bar to allow it, then try again.';
        } else if (name === 'NotFoundError') {
          msg = 'No camera was found on this device.';
        } else if (name === 'NotReadableError') {
          msg = 'Your camera is being used by another app. Close it and try again.';
        }
        setErrMsg(msg);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      try { controlsRef.current?.stop(); } catch { /* noop */ }
      controlsRef.current = null;
      readerRef.current = null;
    };
  }, [open, onResult, filter]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center px-4"
      data-testid="barcode-scanner-modal"
    >
      <div className="relative w-full max-w-sm bg-[#0F172A] rounded-3xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 pt-4 pb-2 text-white">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-emerald-400" />
            <span className="font-display text-lg">Scan barcode</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            data-testid="barcode-scanner-close"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="relative aspect-[3/4] bg-black">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
          />
          {status === 'starting' && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Starting camera…
            </div>
          )}
          {status === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white">
              <Camera className="w-8 h-8 text-red-400 mb-3" />
              <div className="text-sm leading-relaxed">{errMsg}</div>
              <Button
                onClick={onClose}
                variant="ghost"
                className="mt-4 text-white border border-white/20 hover:bg-white/10"
              >
                Close
              </Button>
            </div>
          )}
          {status === 'scanning' && (
            <>
              {/* Targeting box */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute left-[10%] right-[10%] top-1/2 -translate-y-1/2 aspect-[5/2] border-2 border-emerald-400 rounded-2xl">
                  <div className="absolute -top-px left-0 right-0 h-1 bg-emerald-400/70 animate-pulse rounded" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-center text-white text-xs bg-gradient-to-t from-black/80 to-transparent">
                {helperText || 'Point the camera at the barcode'}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BarcodeScannerModal;
